import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Download, Users, GraduationCap, Award, TrendingUp, CheckCircle2, Building2, BookOpen } from "lucide-react";

export default function CreatorAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("companies");

  useEffect(() => {
    api.get("/creator/analytics").then(r => setData(r.data)).catch(() => {});
  }, []);

  const exportCSV = async () => {
    const r = await api.get(`/creator/analytics/export`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `forge-creator-analytics.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <Layout><div className="px-10 py-20">Loading…</div></Layout>;
  const r = data.rollup;

  return (
    <Layout>
      <div className="px-10 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-2">Creator analytics</div>
            <h1 className="editorial-h1 text-5xl">Your reach.</h1>
            <p className="text-[#52525B] mt-2 max-w-xl">Every learner, every company, every score for the training you've created.</p>
          </div>
          <button onClick={exportCSV} data-testid="creator-export-csv" className="btn-secondary"><Download size={14}/> Export CSV</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-6 mb-10">
          <Stat label="Courses" value={r.total_courses} icon={BookOpen} testid="creator-stat-courses" />
          <Stat label="Companies" value={r.companies} icon={Building2} testid="creator-stat-companies" />
          <Stat label="Learners" value={r.active_learners} icon={Users} testid="creator-stat-learners" />
          <Stat label="Enrollments" value={r.total_enrollments} icon={GraduationCap} testid="creator-stat-enrollments" />
          <Stat label="Completions" value={`${r.completions}`} sub={`${r.completion_rate}% rate`} icon={Award} testid="creator-stat-completions" />
          <Stat label="Pass rate" value={r.pass_rate !== null ? `${r.pass_rate}%` : "—"} sub={r.tests_passed + r.tests_failed > 0 ? `${r.tests_passed}✓ · ${r.tests_failed}✗` : "no tests yet"} icon={CheckCircle2} testid="creator-stat-pass" accent />
        </div>

        <div className="flex gap-2 border-b border-[#E4E4E7] mb-6">
          {[
            { id: "companies", label: "By company" },
            { id: "courses", label: "By course" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`creator-tab-${t.id}`}
              className={`px-5 py-3 font-display text-sm border-b-2 transition-colors ${tab === t.id ? "border-[#E63946] text-[#0A0A0A]" : "border-transparent text-[#52525B] hover:text-[#0A0A0A]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "companies" ? (
          <div className="editorial-card-static overflow-x-auto">
            <table className="w-full text-sm" data-testid="creator-companies-table">
              <thead className="bg-[#F9F9F8] text-left">
                <tr>
                  <Th>Company</Th>
                  <Th>Learners</Th>
                  <Th>Enrollments</Th>
                  <Th>Completed</Th>
                  <Th>Completion %</Th>
                  <Th>Pass rate</Th>
                  <Th>Avg progress</Th>
                </tr>
              </thead>
              <tbody>
                {data.companies.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-[#52525B]">No companies enrolled yet. Generate a free join link from your course builder to get started.</td></tr>
                ) : data.companies.map(c => (
                  <tr key={c.org_id} className="border-t border-[#E4E4E7]" data-testid={`creator-company-${c.org_id}`}>
                    <Td><span className="font-serif text-lg">{c.name}</span></Td>
                    <Td>{c.learner_count}</Td>
                    <Td>{c.enrolled}</Td>
                    <Td>{c.completed}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#E63946]" style={{ width: `${c.completion_rate}%` }}/></div>
                        <span className="overline">{c.completion_rate}%</span>
                      </div>
                    </Td>
                    <Td>{c.pass_rate !== null ? <span className={`overline ${c.pass_rate >= 80 ? "text-[#166534]" : c.pass_rate >= 50 ? "text-[#854D0E]" : "text-[#991B1B]"}`}>{c.pass_rate}%</span> : <span className="text-[#52525B]">—</span>}</Td>
                    <Td>{c.avg_progress}%</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="editorial-card-static overflow-x-auto">
            <table className="w-full text-sm" data-testid="creator-courses-table">
              <thead className="bg-[#F9F9F8] text-left">
                <tr>
                  <Th>Course</Th>
                  <Th>Category</Th>
                  <Th>Enrolled</Th>
                  <Th>Completed</Th>
                  <Th>Completion %</Th>
                  <Th>Pass rate</Th>
                  <Th>Avg score</Th>
                </tr>
              </thead>
              <tbody>
                {data.courses.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-[#52525B]">No enrollments yet.</td></tr>
                ) : data.courses.map(c => (
                  <tr key={c.course_id} className="border-t border-[#E4E4E7]">
                    <Td>
                      <div className="font-serif text-lg">{c.title}</div>
                      <div className="overline mt-1">pass ≥ {c.passing_score}%</div>
                    </Td>
                    <Td><span className="overline">{c.category}</span></Td>
                    <Td>{c.enrolled}</Td>
                    <Td>{c.completed}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#E63946]" style={{ width: `${c.completion_rate}%` }}/></div>
                        <span className="overline">{c.completion_rate}%</span>
                      </div>
                    </Td>
                    <Td>{c.pass_rate !== null ? <span className={`overline ${c.pass_rate >= 80 ? "text-[#166534]" : c.pass_rate >= 50 ? "text-[#854D0E]" : "text-[#991B1B]"}`}>{c.pass_rate}%</span> : <span className="text-[#52525B]">—</span>}</Td>
                    <Td>{c.avg_score !== null ? c.avg_score : <span className="text-[#52525B]">—</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

const Th = ({ children }) => <th className="px-5 py-3 overline">{children}</th>;
const Td = ({ children }) => <td className="px-5 py-3">{children}</td>;

function Stat({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div className={`editorial-card-static p-5 ${accent ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className={`overline ${accent ? "text-[#E63946]" : ""}`}>{label}</div>
        <Icon size={16} strokeWidth={1.5}/>
      </div>
      <div className="font-display text-3xl font-bold">{value}</div>
      {sub && <div className={`overline mt-2 ${accent ? "text-zinc-500" : "text-[#52525B]"}`}>{sub}</div>}
    </div>
  );
}
